/**
 * Authenticated identity contract — shared between api and web
 * (architecture.md §4.4 "Авторизация и IDOR"; ADR 2026-03-04).
 *
 * This is the EXACT projection the legacy n8n node `DB: Validate Session1`
 * returned for the authenticated principal (docs/source-n8n/sql-queries.md):
 *
 *   SELECT u.id, u.email, u.name, u.role, u.team_id, u.avatar
 *   FROM sessions s JOIN users u ON u.id = s.user_id
 *   WHERE s.token = $1 AND s.expires_at > NOW()
 *
 * Critical security invariant (carried over unchanged): this identity is
 * derived ONLY from the `sessions` row matched by the session token. Any
 * `owner_id` / `user_id` / `role` in a request body is IGNORED. The Fastify
 * `preHandler` auth-hook builds `request.user` from THIS shape and nothing else
 * (`packages/api/src/auth/session.ts` + `plugins/auth.ts`).
 *
 * `password` is deliberately NOT part of the projection — the `users` table has
 * a `password` column, but it must never reach the API surface or `request.user`.
 *
 * The schema is the single source of truth (`AuthIdentity` is `z.infer`-derived),
 * so the runtime validator (the session lookup result parser) and the
 * compile-time type can never drift.
 */
import { z } from 'zod';
/**
 * Known application roles (legacy `users.role` values, used by the IDOR query
 * and `requireRole` guards). `role` is kept a plain string at the type level
 * (the DB column is `varchar(50)`, not an enum), but the canonical values are
 * enumerated here for the role guards and for documentation.
 */
export const APP_ROLES = ['admin', 'teamlead', 'recruiter'];
/**
 * The authenticated principal as projected from `sessions` ⋈ `users`.
 *
 *  - `id`       — `users.id` (varchar(50)); the ONLY trusted owner identifier.
 *  - `email`    — `users.email`.
 *  - `name`     — `users.name`.
 *  - `role`     — `users.role`; drives `requireRole` and the IDOR scope query.
 *  - `team_id`  — `users.team_id` (nullable); used by the teamlead scope.
 *  - `avatar`   — `users.avatar` (nullable); short label/emoji for the UI.
 *
 * Field names are snake_case to match the legacy projection and the DB columns
 * exactly (the front-end already consumed `team_id`/`avatar` in that shape).
 */
export const authIdentitySchema = z.object({
    id: z.string().min(1),
    email: z.string().min(1),
    name: z.string().min(1),
    role: z.string().min(1),
    team_id: z.string().nullable(),
    avatar: z.string().nullable(),
});
/**
 * `POST /auth/login` success body: the freshly minted session token plus the
 * authenticated principal (so the SPA can render the user immediately without a
 * follow-up `GET /auth/me`). Lives here — not in web — because @eer/shared is the
 * single source of truth for request/response contracts (architecture.md §4), and
 * web must never import `zod` directly (only @eer/shared declares it). The web
 * auth transport (`packages/web/src/auth/api.ts`) validates the login body with
 * this schema so a partial response never establishes a session.
 *
 * Parity: legacy n8n `DB: Create Session` returned the session token; the
 * principal mirrors `authIdentitySchema` (the `Validate Session1` projection).
 */
export const loginResponseSchema = z.object({
    session_token: z.string().min(1),
    user: authIdentitySchema,
});
//# sourceMappingURL=auth.js.map